<script lang="ts" setup>
import type { InputCreateAddress, IonSearchbar } from '#build/components';

const emits = defineEmits<{
  select: [address: AddressSuggestion]
}>();

defineExpose({ open });
const hotkeyManager = useHotkeyManager();
const { t } = useEkI18n();
const { focusOnEnter } = useWebInputManager();
const platformStore = usePlatformStore();
const { SearchAddress, GetCountries } = useAddressApi();
const config = useRuntimeConfig();

const isOpen = ref<boolean>(false);
const searchbar = useTemplateRef('searchbar');
const addressSearchModal = useTemplateRef('addressSearchModal');
const createAddressInput = ref<InstanceType<typeof InputCreateAddress>>();
const countryPopoverIsOpen = ref<boolean>(false);
const countryPopoverEvent = ref<Event>();
const query = ref<string>('');
const addressFocus = ref<string>('addressFocus');
const selectedCountryCode = ref<string>(config.public.defaultCountryCode);

const instanceId = useId();
const { data: countries, status: countriesStatus, execute: fetchCountries } = useAsyncData('AddressSearchCountries', getCountryCodes, { immediate: false, default: (): string[] => [] });
const { data: suggestions, status, clear } = useAsyncData(`AddressSearchSuggestions-${instanceId}`, searchAddresses, { immediate: false, default: () => [], watch: [query, selectedCountryCode] });

const pending = computed(() => status.value === 'pending');
const showCountryPicker = computed(() => countries.value.length >= 2);

const searchMessage = computed(() => {
  switch (true) {
    case (query.value.trim().length < 3):
      return t('search.minCharacters', { count: 3 });
    case (query.value.trim().length >= 3 && suggestions.value?.length === 0 && status.value !== 'pending'):
      return t('search.noResults', { item: t('worksheet.address').toLowerCase() });
    case (status.value === 'pending'):
      return t('search.searching');
    default:
      return '';
  }
});

const searchMessageSubheader = computed(() => {
  return query.value.trim().length >= 3 && suggestions.value?.length === 0 && status.value !== 'pending'
    ? t('search.noResultsText')
    : '';
});

function countryFlagClass(code: string) {
  return `fi fi-${code.toLowerCase()}`;
}

async function getCountryCodes(): Promise<string[]> {
  const response = await GetCountries();

  return response?.Data ?? [];
}

async function searchAddresses(): Promise<AddressSuggestion[]> {
  if (query.value.trim().length < 3) {
    return [];
  }

  const response = await SearchAddress({
    Query: query.value,
    CountryCode: selectedCountryCode.value,
    MaxResults: 50
  });

  return response?.Data ?? [];
}

async function open() {
  isOpen.value = true;

  if (countriesStatus.value !== 'success') {
    await fetchCountries();
  }

  if (countries.value.length > 0 && !countries.value.includes(selectedCountryCode.value)) {
    selectedCountryCode.value = countries.value[0];
  }

  if (platformStore.isWeb) {
    hotkeyManager.registerModalHotkeys({
      [Hotkeys.Escape]: () => reset()
    }, addressSearchModal.value?.$el);
  }
}

function reset() {
  isOpen.value = false;
  query.value = '';
  clear();

  if (platformStore.isWeb) {
    hotkeyManager.clearModalHotkeys(addressSearchModal.value?.$el);
  }
}

function select(suggestion: AddressSuggestion) {
  emits('select', suggestion);
  reset();
}

function openCountryPopover(event: Event) {
  countryPopoverEvent.value = event;
  countryPopoverIsOpen.value = true;
}

function handleCreateAddress(address: CreateAssociatedAddressResponseDTO) {
  select({
    Id: address.ID.toString(),
    DisplayText: [address.Address, address.Address2].filter(Boolean).join(', ') + `, ${address.ZipCode.Code} ${address.ZipCode.CityName}`,
    Type: '',
    HouseNumber: '',
    StreetName: address.Address,
    Door: address.Address2,
    Floor: '',
    SupplementaryCity: '',
    ZipCode: address.ZipCode.Code,
    City: address.ZipCode.CityName,
    MunicipalityCode: '',
    CountryCode: selectedCountryCode.value,
    CountryName: address.ZipCode.Country.CountryName
  });
}

function onEnter() {
  if (!platformStore.isWeb) {
    return;
  }

  focusOnEnter('.' + addressFocus.value);
}
</script>

<template>
  <Modal
    ref="addressSearchModal"
    :open="isOpen"
    :title="t('titles.searchForAddress')"
    animation-direction="vertical"
    :close-override="reset"
    @did-present="searchbar?.$el.setFocus();"
    @handle-dismiss="reset"
  >
    <template #headerButtons>
      <IonButton
        v-if="showCountryPicker"
        @click="openCountryPopover"
      >
        <span
          class="country-flag"
          :class="countryFlagClass(selectedCountryCode)"
        />
      </IonButton>
      <IonButton @click="createAddressInput?.open()">
        <IonIcon
          slot="icon-only"
          class="fa-light fa-fw fa-location-plus"
        />
      </IonButton>
    </template>

    <template #subheader>
      <IonToolbar>
        <IonSearchbar
          ref="searchbar"
          v-model="query"
          :placeholder="t('search.address')"
          :debounce="500"
          @keyup.enter="onEnter"
        />
        <IonProgressBar
          v-show="pending"
          type="indeterminate"
        />
      </IonToolbar>
    </template>

    <IonContent>
      <UserMessagesSearch
        v-if="searchMessage.length > 0"
        icon="fa-fw fa-light fa-magnifying-glass"
        :header="searchMessage"
        :subheader="searchMessageSubheader"
      />
      <IonList v-else>
        <IonItem
          v-for="suggestion in suggestions"
          :key="suggestion.Id"
          :class="addressFocus"
          lines="full"
          button
          @click="select(suggestion)"
        >
          {{ suggestion.DisplayText }}
        </IonItem>
      </IonList>
    </IonContent>
  </Modal>

  <IonPopover
    v-if="showCountryPicker"
    :is-open="countryPopoverIsOpen"
    :event="countryPopoverEvent"
    reference="event"
    side="bottom"
    alignment="end"
    size="auto"
    @did-dismiss="countryPopoverIsOpen = false"
  >
    <IonRadioGroup
      v-model="selectedCountryCode"
      @ion-change="countryPopoverIsOpen = false"
    >
      <IonItem
        v-for="code in countries"
        :key="code"
        button
        lines="full"
        @click="selectedCountryCode = code; countryPopoverIsOpen = false"
      >
        <span
          slot="start"
          class="country-flag"
          :class="countryFlagClass(code)"
        />
        <IonRadio
          slot="end"
          color="primary"
          :value="code"
        />
      </IonItem>
    </IonRadioGroup>
  </IonPopover>

  <InputCreateAddress
    ref="createAddressInput"
    @created="handleCreateAddress"
  />
</template>

<style scoped>
.country-flag {
  display: inline-block;
  width: 1.5rem;
  height: 1.125rem;
}
</style>
